// @flow
import axios from 'axios';
import { history } from './myHistory';

axios.defaults.xsrfCookieName = process.env.REACT_APP_COOKIE_NAME;
axios.defaults.xsrfHeaderName = 'X-CSRFTOKEN';
axios.defaults.withCredentials = true;

/**
 * Build a human-readable string from DRF error payloads.
 * Never append raw objects (they become "[object Object]").
 */
export function formatAxiosErrorMessage(data: any, status: ?number): string {
  if (data == null || data === '') {
    return status === 409
      ? 'This item was updated by someone else. Refreshing.'
      : 'Error Completing Request';
  }
  if (typeof data === 'string') {
    return data;
  }
  // Known concurrency conflict shape from schedule live-feed work
  if (status === 409 || data.code === 'participant_conflict') {
    return (
      data.message ||
      'Someone else updated this participant. The schedule will refresh.'
    );
  }
  if (typeof data === 'object') {
    if (typeof data.detail === 'string') {
      return data.detail;
    }
    if (typeof data.message === 'string') {
      return data.message;
    }
    if (typeof data.error === 'string') {
      return data.error;
    }
    const parts = [];
    Object.keys(data).forEach((key) => {
      const value = data[key];
      if (value == null) return;
      if (typeof value === 'string' || typeof value === 'number') {
        // Skip opaque machine codes alone if we have better text later
        if (key === 'code' || key === 'participantUserId') return;
        parts.push(String(value));
      } else if (Array.isArray(value)) {
        const flat = value
          .filter((v) => typeof v === 'string' || typeof v === 'number')
          .join(', ');
        if (flat) parts.push(flat);
      }
      // skip plain objects (e.g. nested participant on 409)
    });
    if (parts.length) {
      return parts.join('\n');
    }
  }
  return status === 409
    ? 'Someone else updated this participant. The schedule will refresh.'
    : 'Error Completing Request';
}

export default function initializeAxios() {
  axios.interceptors.request.use(
    (config: any) => {
      config.headers['Content-Type'] = 'application/json';
      return config;
    },
    (error) => {
      return Promise.reject({ message: 'Error Making Request' });
    }
  );

  axios.interceptors.response.use(
    function (response) {
      return response;
    },
    function (error) {
      if (error.response) {
        if (error.response.status === 403) {
          setTimeout(() => {
            history.push('/login');
          });
        }
        const message = formatAxiosErrorMessage(
          error.response.data,
          error.response.status
        );
        return Promise.reject({
          message,
          status: error.response.status,
          data: error.response.data,
          isConflict: error.response.status === 409,
        });
      }
      return Promise.reject({
        message: (error && error.message) || 'Error Completing Request',
      });
    }
  );
}
